import type { FormProps } from "ra-core";

import { esAparcada } from "../dominio/etapas";
import type { Etapa, Negocio } from "../dominio/tipos";

/** Errores de formulario por campo, con clave i18n (`nevora.ficha.errores.*`). */
export type ErroresFormulario = Record<string, string>;

/**
 * Tipo del prop `validate` de `Form`/`SimpleForm`. Es la intersección del `validate` de
 * ra-core (devuelve `{ campo: mensaje }`) con el de react-hook-form (que ra-core no usa:
 * lo extrae antes de llamar a useForm). Los tipos son incompatibles entre sí, así que el
 * validador se adapta con un cast en un único sitio.
 */
type ValidadorFormulario = NonNullable<FormProps["validate"]>;
export const comoValidador = (
  fn: (datos: unknown) => ErroresFormulario,
): ValidadorFormulario => fn as unknown as ValidadorFormulario;

/** Fechas obligatorias según la etapa elegida (mismas que pide el cambio de etapa a mano). */
const erroresPorEtapa = (valores: Partial<Negocio>): ErroresFormulario => {
  const errores: ErroresFormulario = {};
  if (valores.etapa === "consultoria_agendada" && !valores.fecha_reunion) {
    errores.fecha_reunion = "nevora.ficha.errores.falta_fecha_reunion";
  }
  if (
    valores.etapa === "presentacion_agendada" &&
    !valores.fecha_presentacion
  ) {
    errores.fecha_presentacion =
      "nevora.ficha.errores.falta_fecha_presentacion";
  }
  if (valores.etapa === "largo_plazo" && !valores.fecha_vuelta) {
    errores.fecha_vuelta = "nevora.ficha.errores.falta_fecha_vuelta";
  }
  return errores;
};

/**
 * Validación del alta (petición de Robert, 25/09/2026): si hay siguiente paso, su fecha es
 * obligatoria; y las fechas que pida la etapa en la que nace. Funciones puras, sin React.
 */
export const validarAlta = (datos: unknown): ErroresFormulario => {
  const valores = datos as Partial<Negocio>;
  const errores: ErroresFormulario = erroresPorEtapa(valores);
  if (valores.siguiente_paso?.trim() && !valores.fecha_siguiente_paso) {
    errores.fecha_siguiente_paso = "nevora.ficha.errores.falta_fecha_paso";
  }
  return errores;
};

/**
 * Ficha: lo del alta + no reactivar una aparcada con «no volver a contactar» (misma regla
 * que validarCambioEtapa y el trigger proyectos_antes).
 */
export const validarFicha =
  (anterior: { etapa: Etapa }) =>
  (datos: unknown): ErroresFormulario => {
    const valores = datos as Partial<Negocio>;
    const errores = validarAlta(valores);
    if (
      valores.etapa &&
      esAparcada(anterior.etapa) &&
      !esAparcada(valores.etapa) &&
      valores.no_llamar
    ) {
      errores.etapa = "nevora.etapas_errores.no_llamar";
    }
    return errores;
  };
